import { Button, Input } from '@assembly-js/design-system';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { useUpdateAppSettings } from 'src/hooks/useUpdateAppSettings';

interface RenameAppFormProps {
  moduleId: string;
  onClose: () => void;
}
const NAME_ERROR = 'Name required';
const MAX_NAME_LENGTH = 50;
export const RenameAppForm = ({ moduleId, onClose }: RenameAppFormProps) => {
  const { updateSettings, currentTitle, currentIcon } =
    useUpdateAppSettings(moduleId);

  const [name, setName] = useState(currentTitle);
  const [icon, setIcon] = useState(currentIcon ?? 'dashboard');
  const [nameError, setNameError] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && currentTitle) {
      setName(currentTitle);
      setIcon(currentIcon ?? 'dashboard');
      initialized.current = true;
    }
  }, [currentTitle, currentIcon]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length > MAX_NAME_LENGTH) return;
    setName(val);
    if (!val.trim()) setNameError(NAME_ERROR);
    else if (nameError) setNameError(null);
  };

  const handleNameBlur = () => {
    if (!name.trim()) setNameError(NAME_ERROR);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(NAME_ERROR);
      return;
    }
    onClose();
    updateSettings({ name: trimmed, icon });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2">
        <SuggestIconPicker
          value={icon}
          onChange={setIcon}
          appName={name}
          className="w-7 h-7 shrink-0 mt-px"
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <Input
            autoFocus
            className={clsx('h-7 text-sm', nameError && 'border-error-primary')}
            value={name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            maxLength={MAX_NAME_LENGTH}
          />
          {nameError && (
            <ErrorMessage message={nameError} className="text-sm" />
          )}
        </div>
        <Button
          type="submit"
          size="sm"
          label="Save"
          className="h-7 shrink-0 mt-px"
          disabled={!name.trim() || name.trim() === currentTitle}
        />
      </div>
      {name.length >= 40 && (
        <span className="text-xs text-gray-500 text-right">
          {name.length}/{MAX_NAME_LENGTH}
        </span>
      )}
    </form>
  );
};
